import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Ticket, Trophy, Repeat, Star, Check, PauseCircle, PlayCircle } from "lucide-react";
import api, { getErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { NeonButton, NeonCard, NeonPrice, SectionTitle, Spinner } from "../components/Neon";
import { gbp } from "../lib/format";

// Tries the new multi-tier endpoint; falls back to the single-plan endpoint.
async function loadPlans() {
  try {
    const { data } = await api.get("/subscription-plans");
    if (Array.isArray(data) && data.length) return { multi: true, plans: data };
  } catch (e) { /* endpoint not deployed yet -> fallback */ }
  return { multi: false, plans: [] };
}

function defaultPerks(plan) {
  return [
    { icon: Ticket, t: `${plan.free_tickets || 0} free tickets into selected competitions` },
    { icon: Trophy, t: `Auto-entry into every ${plan.draw_name || "monthly"} ${gbp(plan.monthly_prize)} draw` },
    { icon: Repeat, t: "Use free tickets every cycle — cancel anytime" },
  ];
}

function PlanCard({ plan, isActive, isPaused, onSubscribe, onCancel, onPause, onResume, busy, highlight }) {
  const perks = Array.isArray(plan.perks) && plan.perks.length
    ? plan.perks.map((p) => ({ icon: Star, t: p }))
    : defaultPerks(plan);
  return (
    <NeonCard variant="green" innerClassName="p-7 h-full flex flex-col" data-testid={`members-plan-${plan.id || "default"}`}>
      {highlight && <div className="self-start mb-2"><span className="rounded-full bg-[var(--neon-pink)] text-black text-[11px] font-bold uppercase px-3 py-1">Most Popular</span></div>}
      <p className="font-display uppercase tracking-widest text-[var(--neon-green)] text-sm">{plan.name || "Members Club"}</p>
      <div className="mt-2 text-center">
        <NeonPrice value={gbp(plan.monthly_prize)} color="pink" className="text-5xl" />
        <p className="mt-1 font-display uppercase text-xl text-white">{plan.draw_name || "Monthly Members Draw"}</p>
      </div>
      <div className="mt-5 space-y-3 flex-1">
        {perks.map((b, idx) => (
          <div key={idx} className="flex items-start gap-3"><b.icon className="h-5 w-5 text-[var(--neon-green)] shrink-0 mt-0.5" /><span className="text-white text-sm">{b.t}</span></div>
        ))}
      </div>
      <div className="mt-6 text-center">
        <div><NeonPrice value={gbp(plan.price)} color="green" className="text-4xl" /> <span className="text-[var(--text-muted)]">/ month</span></div>
        {isActive ? (
          <div className="mt-4">
            <div className="inline-flex items-center gap-2 font-display uppercase">
              {isPaused
                ? <span className="inline-flex items-center gap-2 text-[var(--neon-gold)]"><PauseCircle className="h-5 w-5" /> Paused</span>
                : <span className="inline-flex items-center gap-2 text-[var(--neon-green)]"><Check className="h-5 w-5" /> Your Plan</span>}
            </div>
            <div className="mt-3 flex flex-wrap items-center justify-center gap-3">
              {isPaused
                ? <NeonButton variant="outlineGreen" onClick={onResume} loading={busy} data-testid="members-resume-button"><PlayCircle className="h-4 w-4" /> Resume</NeonButton>
                : <NeonButton variant="outlineCyan" onClick={onPause} loading={busy} data-testid="members-pause-button"><PauseCircle className="h-4 w-4" /> Pause</NeonButton>}
              <NeonButton variant="outlinePink" onClick={onCancel} loading={busy} data-testid="members-cancel-button">Cancel</NeonButton>
            </div>
          </div>
        ) : (
          <div className="mt-4"><NeonButton variant="pink" onClick={() => onSubscribe(plan)} loading={busy} data-testid={`members-subscribe-button${plan.id ? "-" + plan.id : ""}`}>Become a Member</NeonButton></div>
        )}
      </div>
    </NeonCard>
  );
}

export default function Members() {
  const { isAuthed, isVerified, refreshUser } = useAuth();
  const [multi, setMulti] = useState(false);
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [pl, s] = await Promise.all([
      loadPlans(),
      api.get("/subscription").then((r) => r.data).catch(() => null),
    ]);
    setMulti(pl.multi);
    setSub(s);
    if (pl.multi) setPlans(pl.plans);
    else if (s?.plan) setPlans([{ ...s.plan, id: s.plan.id || "default" }]);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const subscribe = async (plan) => {
    if (!isAuthed) { toast.error("Log in to become a member."); return; }
    if (!isVerified) { toast.error("Verify your email & phone first."); return; }
    setBusy(true);
    try {
      await api.post("/subscription/subscribe", plan?.id && plan.id !== "default" ? { plan_id: plan.id } : {});
      toast.success("Welcome to the Members Club!");
      await load(); refreshUser();
    } catch (e) { toast.error(getErr(e)); } finally { setBusy(false); }
  };
  const cancel = async () => {
    if (!window.confirm("Cancel your membership?")) return;
    setBusy(true);
    try { await api.post("/subscription/cancel"); toast.success("Membership cancelled."); await load(); }
    catch (e) { toast.error(getErr(e)); } finally { setBusy(false); }
  };
  const pause = async () => {
    setBusy(true);
    try { await api.post("/subscription/pause"); toast.success("Membership paused — resume anytime."); await load(); refreshUser(); }
    catch (e) { toast.error(getErr(e)); } finally { setBusy(false); }
  };
  const resume = async () => {
    setBusy(true);
    try { await api.post("/subscription/resume"); toast.success("Membership resumed. Welcome back!"); await load(); refreshUser(); }
    catch (e) { toast.error(getErr(e)); } finally { setBusy(false); }
  };

  if (loading) return <Spinner className="min-h-[60vh]" />;

  const status = sub?.status || (sub?.active ? "active" : null);
  const isPaused = status === "paused" || sub?.paused === true;
  const isMember = !!sub?.active || isPaused;
  // Active subs may not carry a plan_id (legacy single-plan); fall back to the only plan.
  const currentPlanId = sub?.plan_id || sub?.current?.plan_id || sub?.plan?.id
    || (plans.length === 1 ? (plans[0].id || "default") : null);
  const activePlanId = isMember ? (currentPlanId || "default") : null;

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10">
      <SectionTitle accent="white" sub={multi ? "Choose the membership tier that suits you. Cancel anytime." : "Auto-entry into the monthly members draw with free tickets every cycle."}>
        <Star className="inline h-7 w-7 mr-2 text-[var(--neon-pink)]" />Members Club
      </SectionTitle>

      {plans.length === 0 ? (
        <NeonCard variant="soft" className="mt-6" innerClassName="p-8 text-center"><p className="text-[var(--text-muted)]">No membership plans are available right now.</p></NeonCard>
      ) : (
        <div className={`mt-6 grid gap-5 ${plans.length > 1 ? "md:grid-cols-2 lg:grid-cols-3" : "max-w-2xl mx-auto"}`}>
          {plans.map((plan, i) => (
            <PlanCard key={plan.id || i} plan={plan} isActive={activePlanId === (plan.id || "default")} isPaused={isPaused && activePlanId === (plan.id || "default")} onSubscribe={subscribe} onCancel={cancel} onPause={pause} onResume={resume} busy={busy} highlight={plans.length > 1 && i === Math.floor(plans.length / 2)} />
          ))}
        </div>
      )}

      <div className="mt-8">
        <p className="font-display uppercase tracking-widest text-white text-lg">Free tickets apply to</p>
        <div className="mt-3 space-y-2" data-testid="members-club-free-tickets-list">
          {(sub?.eligible_competitions || []).length === 0 ? <p className="text-sm text-[var(--text-muted)]">No eligible competitions right now.</p> :
            sub.eligible_competitions.map((c) => (
              <Link key={c.id} to={`/competitions/${c.id}`} className="flex items-center justify-between rounded-xl border border-white/10 bg-[var(--surface)] px-4 py-3 hover:border-[var(--neon-cyan)]">
                <span className="flex items-center gap-3 text-white"><Star className="h-4 w-4 text-[var(--neon-cyan)]" /> {c.title}</span>
                <span className="text-[var(--neon-cyan)]">›</span>
              </Link>
            ))}
        </div>
      </div>
    </div>
  );
}
